<template>
  <div class="flex flex-col p-6 ml-4 lg:ml-64">
    <h2 class="text-xl text-gray-900 font-semibold mb-4">
      Gerenciamento de Controles
    </h2>
    <!-- Campo de busca -->
    <div class="mb-6">
      <h3 class="text-lg font-semibold">Controles</h3>
      <ul class="border rounded p-4 bg-white">
        <div class="py-2 flex items-center justify-between">
          <input
            type="text"
            v-model="searchQuery"
            class="p-2 border border-gray-300 rounded"
            placeholder="Pesquisar (ID, Nome ou Tipo)..."
          />
        </div>

        <!-- Botão Adicionar Controle -->
        <button
          class="btn-primary px-2 py-1 w-32 bg-blue-500 text-white rounded mb-4 text-xs"
          @click="openModal"
        >
          Adicionar Controle
        </button>

        <!-- Tabela de listagem dos controles -->
        <table
          id="controls-table"
          class="min-w-full table-auto border-collapse text-black text-xs rounded-md border border-gray-300"
        >
          <thead>
            <tr>
              <th class="border px-4 py-2">ID</th>
              <th class="border px-4 py-2">Nome do Controle</th>
              <th class="border px-4 py-2">Tipos de Controles</th>
              <th class="border px-4 py-2">ISO/IEC 270001:2022</th>
              <th class="border px-4 py-2">Tipo de Controle</th>
              <th class="border px-4 py-2">Propriedade de SI</th>
              <th class="border px-4 py-2 truncate">
                Conceito de Segurança Cibernética
              </th>
              <th class="border px-4 py-2">Capacidades Operacionais</th>
              <th class="border px-4 py-2">Domínio de SI</th>
              <th class="border px-4 py-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="control in filteredControllers" :key="control._id">
              <td class="border px-4 py-2">{{ control.ControllerId }}</td>
              <td class="border px-4 py-2 truncate w-16 max-w-[16rem]">
                {{ control.ControllerName }}
              </td>
              <td class="border px-4 py-2">{{ control.TypesOfControls }}</td>
              <td class="border px-4 py-2 truncate max-16 max-w-[16rem]">
                {{ control.Statement }}
              </td>
              <td class="border px-4 py-2">
                {{ control.FunctionalControlTypes[0] }}
              </td>
              <td class="border px-4 py-2 truncate w-16 max-w-[16rem]">
                {{ control.PropertyofSI[0] }}
              </td>
              <td class="border px-4 py-2">
                {{ control.CybersecurityConcept[0] }}
              </td>
              <td class="border px-4 py-2">
                {{ control.OperationalCapabilities[0] }}
              </td>
              <td class="border px-4 py-2">{{ control.DomainsDeSI[0] }}</td>
              <td class="border px-4 py-2">
                <div class="flex gap-2">
                  <button
                    class="px-2 py-1 rounded text-xs text-black"
                    @click="handleEdit(control)"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    class="px-2 py-1 rounded text-xs text-black"
                    @click="confirmarRemocao(control._id)"
                  >
                    🗑️ Remover
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </ul>
    </div>

    <!-- Modal de Cadastro/Edição -->
    <div
      v-if="modalAberto"
      class="modal-overlay fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center ml-70"
    >
      <div
        class="modal bg-white p-6 rounded shadow-lg w-[100%] max-w-4xl h-[90%] relative overflow-auto"
      >
        <button
          class="px-4 py-2 bg-red-500 rounded hover:bg-amber-800"
          @click="closedModal"
        >
          Fechar
        </button>
        <h2 class="text-2xl font-semibold mb-4">
          {{ isEditing ? 'Editar Controle' : 'Novo Controle' }}
        </h2>
        <h1>{{ NomeDaEmpresa }}</h1>
        <form @submit.prevent="handleSubmit">
          <label class="block text-sm font-medium text-black">ID</label>
          <textarea
            v-model="formData.ControllerId"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            required
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="handleInput"
            @keydown="onlyNumbers"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >Nome do Controle</label
          >
          <textarea
            type="text"
            v-model="formData.ControllerName"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >Tipos de Controles</label
          >
          <textarea
            type="text"
            v-model="formData.TypesOfControls"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >ISO/IEC 27001:2022</label
          >
          <textarea
            type="text"
            v-model="formData.Statement"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >Tipo de Controle</label
          >
          <textarea
            type="text"
            v-model="formData.FunctionalControlTypes"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >Propriedade de SI</label
          >
          <textarea
            type="text"
            v-model="formData.PropertyofSI"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >Conceito de Segurança Cibernética</label
          >
          <textarea
            type="text"
            v-model="formData.CybersecurityConcept"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >Capacidades Operacionais</label
          >
          <textarea
            type="text"
            v-model="formData.OperationalCapabilities"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>

          <label class="block text-sm font-medium text-black"
            >Domínio de SI</label
          >
          <textarea
            type="text"
            v-model="formData.DomainsDeSI"
            class="w-full p-2 border border-gray-300 rounded mb-4 resize-none text-black"
            :style="{
              minWidth: '750px',
              maxWidth: '100%',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              height: 'auto',
            }"
            rows="1"
            @input="adjustHeight($event)"
          >
          </textarea>
          <div class="modal-footer flex justify-end mt-4">
            <button
              type="submit"
              class="btn-success bg-blue-500 text-white py-2 px-4 rounded"
            >
              Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createController,
  deleteController,
  getControllers,
  updateController,
} from '@/services/security-controls/api.controllers'

export default {
  props: {
    EmpresaID: { type: String, required: true },
    NomeDaEmpresa: { type: String, required: true },
  },
  data() {
    return {
      searchQuery: '',
      modalAberto: false,
      isEditing: false,
      formData: {
        ControllerId: '',
        ControllerName: '',
        TypesOfControls: '',
        Statement: '',
        FunctionalControlTypes: '',
        PropertyofSI: '',
        CybersecurityConcept: '',
        OperationalCapabilities: '',
        DomainsDeSI: '',
      },
      editingIndex: null,
      controllers: [],
    }
  },
  computed: {
    filteredControllers() {
      return this.controllers.filter(
        (control) =>
          control.ControllerName.toLowerCase().includes(
            this.searchQuery.toLowerCase()
          ) || control.ControllerId.toString().includes(this.searchQuery)
      )
    },
  },
  methods: {
    handleInputID(event) {
      this.onlyNumbers(event)
      this.validateInput(event)
    },
    confirmarRemocao(id) {
      const confirmacao = confirm(
        'Tem certeza que deseja remover este controle?'
      )
      if (confirmacao) {
        this.removeController(id)
      }
    },
    removeController(id) {
      deleteController(id).then(() => {
        this.loadControllers()
      })
    },

    onlyNumbers(event) {
      const validKeys = [
        'Backspace',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
      ]
      const char = event.key

      if (validKeys.includes(char)) return

      if (
        !/^[0-9.]$/.test(char) ||
        (char === '.' && event.target.value.includes('.'))
      ) {
        event.preventDefault()
      }
    },

    validateInput(event) {
      let value = event.target.value.trim()

      if (/^\d+(\.\d+)?$/.test(value)) {
        this.formData.ControllerId = value.toString()
      } else {
        this.formData.ControllerId = ''
      }
    },

    adjustHeight(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    },
    async loadControllers() {
      try {
        const data = await getControllers()
        this.controllers = data
          .map((control) => ({
            ...control,
            ControllerIdParts: control.ControllerId.split('.').map(Number),
          }))
          .sort((a, b) => {
            for (
              let i = 0;
              i <
              Math.max(a.ControllerIdParts.length, b.ControllerIdParts.length);
              i++
            ) {
              const partA = a.ControllerIdParts[i] ?? 0
              const partB = b.ControllerIdParts[i] ?? 0
              if (partA !== partB) {
                return partA - partB
              }
            }
            return 0
          })
      } catch (error) {
        console.error('Erro ao carregar controladores:', error)
      }
    },

    openModal() {
      this.modalAberto = true
      this.isEditing = false
      this.formData = {
        ControllerId: '',
        ControllerName: '',
        TypesOfControls: '',
        Statement: '',
        FunctionalControlTypes: '',
        PropertyofSI: '',
        CybersecurityConcept: '',
        OperationalCapabilities: '',
        DomainsDeSI: '',
      }
    },
    closedModal() {
      this.modalAberto = false
    },
    handleEdit(controller) {
      this.formData = { ...controller }
      this.isEditing = true
      this.modalAberto = true
    },
    async handleSubmit() {
      try {
        if (this.isEditing) {
          await updateController(this.formData._id, this.formData)
        } else {
          await createController(this.formData)
        }
        this.closedModal()
        await this.loadControllers()
      } catch (error) {
        console.error('Erro ao salvar controle:', error)
      }
    },
    async confirmRemoval(id) {
      if (confirm('Tem certeza que deseja remover este controle?')) {
        try {
          await deleteController(id)
          await this.loadControllers()
        } finally {
        }
      }
    },
  },
  mounted() {
    this.loadControllers()
  },
}
</script>
